/**
 * i18n Configuration
 * 
 * ISO-639-1 language codes for top 20 global languages
 * Maximizes worldwide coverage, digital usage, and commercial value
 */

export interface LanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: Record<string, LanguageInfo> = {
  en: { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  zh: { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', direction: 'ltr' },
  hi: { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr' },
  ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr' },
  bn: { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', direction: 'ltr' },
  pt: { code: 'pt', name: 'Portuguese (Brazilian)', nativeName: 'Português (Brasil)', direction: 'ltr' },
  ru: { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' },
  ur: { code: 'ur', name: 'Urdu', nativeName: 'اردو', direction: 'rtl' },
  id: { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', direction: 'ltr' },
  ja: { code: 'ja', name: 'Japanese', nativeName: '日本語', direction: 'ltr' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  sw: { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', direction: 'ltr' },
  tr: { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
  vi: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', direction: 'ltr' },
  ko: { code: 'ko', name: 'Korean', nativeName: '한국어', direction: 'ltr' },
  it: { code: 'it', name: 'Italian', nativeName: 'Italiano', direction: 'ltr' },
  tl: { code: 'tl', name: 'Filipino (Tagalog)', nativeName: 'Filipino', direction: 'ltr' },
  fa: { code: 'fa', name: 'Persian (Farsi)', nativeName: 'فارسی', direction: 'rtl' },
  mi: { code: 'mi', name: 'Maori', nativeName: 'Te Reo Māori', direction: 'ltr' },
  sm: { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', direction: 'ltr' },
  to: { code: 'to', name: 'Tongan', nativeName: 'Lea Fakatonga', direction: 'ltr' },
};

export const DEFAULT_LANGUAGE = 'en';
export const FALLBACK_LANGUAGE = 'en';

export const LANGUAGE_STORAGE_KEY = 'app_language';

/**
 * Get browser language or fallback to default
 */
export const detectBrowserLanguage = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLang = navigator.language || (navigator as any).userLanguage;
  if (!browserLang) return DEFAULT_LANGUAGE;
  
  // Extract language code (e.g., 'en-US' -> 'en')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Check if we support this language
  if (SUPPORTED_LANGUAGES[langCode]) {
    return langCode;
  }
  
  return DEFAULT_LANGUAGE;
};

/**
 * Get stored language preference or detect from browser
 */
export const getInitialLanguage = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES[stored]) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  
  return detectBrowserLanguage();
};

/**
 * Store language preference
 */
export const storeLanguagePreference = (languageCode: string): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
  } catch (error) {
    console.warn('Failed to store language preference:', error);
  }
};

